import { isDefined } from '@enterprise/core';

import type { StagedFileRowProps } from '#uploader/components/types/staged-file-row.props';

import { resolveFilename } from '#uploader/utilities/resolve-filename';

/**
 * Custom hook that computes configuration and derived state for a staged file row in the uploader component.
 *
 * @param params - The staged file row properties, excluding `filenameSuffixLabel`, `onRemove`, and `onSuffixChange`.
 * @param params.disabled - Whether the row is disabled.
 * @param params.fileIndex - The index of the file in the list.
 * @param params.outputFilenameTemplate - Template or function to generate the output filename.
 * @param params.progress - The current progress percentage of the file operation.
 * @param params.showSuffixInput - Whether to show the suffix input field.
 * @param params.staged - The staged file object, including file and suffix.
 * @param params.status - The current status of the file (e.g., 'idle', 'compressing', 'error', etc.).
 * @param params.totalFiles - The total number of files being processed.
 *
 * @returns An object containing:
 * - `canInteract`: Whether the user can interact with the row (e.g., edit or remove).
 * - `isDone`: Whether the file operation is complete.
 * - `isProcessing`: Whether the file is currently being processed.
 * - `primaryName`: The primary display name for the file (resolved or original).
 * - `resolvedName`: The resolved output filename, if applicable.
 * - `resolvePreviewFilename`: Function to resolve the preview filename based on template, suffix, and file info.
 * - `showInput`: Whether to show the suffix input field.
 */
export const useStagedFileRowConfiguration = ({
  disabled = false,
  fileIndex,
  outputFilenameTemplate,
  progress,
  showSuffixInput,
  staged,
  status,
  totalFiles,
}: Omit<StagedFileRowProps, 'filenameSuffixLabel' | 'onRemove' | 'onSuffixChange'>) => {
  const isProcessing = status === 'compressing' && isDefined(progress) && progress < 100;
  const isDone = progress === 100;
  const canInteract = !disabled && (status === 'idle' || status === 'error' || status === 'cancelled');
  const showInput = showSuffixInput && canInteract;

  const resolvedName = isDefined(outputFilenameTemplate)
    ? resolveFilename(outputFilenameTemplate, staged.suffix, fileIndex, totalFiles, staged.file.name)
    : null;

  const primaryName = resolvedName ?? staged.file.name;

  return {
    canInteract,
    isDone,
    isProcessing,
    primaryName,
    resolvedName,
    showInput,
  };
};
